import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import CourseContactForm from './CourseContactForm';
import { CoursesContext } from '../../context/CoursesContext';
import './course.scss';
import './course-mobile.scss';
import { Helmet } from 'react-helmet-async';

const Course = () => {
    
    const { code, title } = useParams(); // comes from the URL /:code/:title : title is really just here for SEO
    const [course, setCourse] = useState([]);
    const [courseSchema, setCourseSchema] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [sourceCategoryName, setSourceCategoryName] = useState(null);    const location = useLocation();
    const { allCourses, dataLoaded } = useContext(CoursesContext);
    
    // Function to format category name for display
    const formatCategoryName = (category) => {
        return category
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };    useEffect(() => {
        // Get the referring page if it exists
        const referrer = document.referrer;
        const pathname = new URL(referrer, window.location.origin).pathname;
        
        // Check if user came from a category page
        if (pathname.startsWith('/categories/')) {
            const categoryFromUrl = pathname.split('/categories/')[1];
            setSourceCategoryName(categoryFromUrl);
        } else if (location.state && location.state.fromCategory) {
            // Alternative: React Router state if available
            setSourceCategoryName(location.state.fromCategory);
        }
        
        setIsLoading(true);
        
        console.log('Fetching course data for code:', code);
        console.log('Courses data status:', {
            allCoursesLoaded: allCourses && allCourses.length > 0,
            allCoursesLength: allCourses?.length || 0,
            codeToFind: code
        });
         
        if (allCourses && allCourses.length > 0) {
            // Find the course with matching code (case-insensitive)
            const courseData = allCourses.find(course => 
                course.code.toLowerCase() === code.toLowerCase()
            );
            
            console.log('Course found:', courseData ? 'Yes' : 'No');
            setCourse(courseData || {});
            
            // Set course schema for structured data
            if (courseData) {
                // Convert courseData.duration (in days) to ISO 8601 duration string (e.g., P3D)
                const durationDays = parseInt(courseData.duration, 10);
                const isoDuration = !isNaN(durationDays) && durationDays > 0 ? `P${durationDays}D` : "P3D";

                // Build audience property if 'who' is present
                let audience = undefined;
                if (courseData.who && courseData.who.trim() !== "") {
                  audience = {
                    "@type": "EducationalAudience",
                    description: courseData.who
                  };
                }

                const courseSchema = {
                  "@context": "https://schema.org",
                  "@type": "Course",
                  name: courseData.title,
                  description: courseData.outline,
                  courseMode: ["onsite", "online"],
                  provider: {
                    "@type": "Organization",
                    name: "Watchelm Consulting Ltd",
                    url: "https://www.watchelm.com"
                  },
                  hasCourseInstance: [
                    {
                      "@type": "CourseInstance",
                      courseMode: "onsite",
                      duration: isoDuration
                    },
                    {
                      "@type": "CourseInstance",
                      courseMode: "online",
                      duration: isoDuration
                    }
                  ],
                  offers: {
                    "@type": "Offer",
                    category: "Paid",
                    availability: "https://schema.org/InStock",
                    priceCurrency: "GBP"
                  },
                  educationalLevel: courseData.level || "Intermediate",
                  teaches: courseData.objectives || [],
                  inLanguage: courseData.language || "en-GB",
                  timeRequired: isoDuration
                };
                if (audience) {
                  courseSchema.audience = audience;
                }
                setCourseSchema(courseSchema);
            }

            // If we don't have a source category yet and the course has categories,
            // use the first category as the source
            if (!sourceCategoryName && courseData && courseData.categories && courseData.categories.length > 0) {
                setSourceCategoryName(courseData.categories[0]);
            }
        } else {
            console.log('All courses data not yet available');
        }
            
        setIsLoading(false);
      
    }, [code, location.state, allCourses, sourceCategoryName]);

    // Fallback: Manually update meta description if React Helmet fails
    useEffect(() => {
        if (course && course.title) {
            const metaDescription = course.metaDescription || 
                `Learn ${course.title} with expert-led training from Watchelm. Professional technical training courses designed for enterprise teams.`;
            
            // Find existing meta description tag and update it as fallback
            const existingMeta = document.querySelector('meta[name="description"]');
            if (existingMeta) {
                existingMeta.setAttribute('content', metaDescription);
            }
        }
    }, [course, course?.metaDescription, course?.title]);

    // First check if data has been loaded from the context
    if (!dataLoaded) {
        return (
            <div className="flow-page">
                <div className="section-container">
                    <h2 className="green-to-red-gradient-font">Loading course data...</h2>
                    <div className="breadcrumb-navigation">
                        <Link to="/">Home</Link>
                        <span className="breadcrumb-separator">/</span>
                        <Link to="/training">Training</Link>
                        <span className="breadcrumb-separator">/</span>
                        <span className="current-page">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }
    
    // Then check if we're still loading course-specific data
    if (isLoading) {
        return (        <div className="flow-page">
            <div className="section-container">
                <h2 className="green-to-red-gradient-font">Loading course information...</h2>
                <div className="breadcrumb-navigation">
                  <Link to="/">Home</Link>
                  <span className="breadcrumb-separator">/</span>
                  <Link to="/training">Training</Link>
                  <span className="breadcrumb-separator">/</span>
                  <span className="current-page">Loading...</span>
                </div>
            </div>
        </div>
        );
    }
    
    // Handle case when course data is not found
    if (!isLoading && (!course || Object.keys(course).length === 0)) {
        return (
            <div className="flow-page">
                <div className="section-container">
                    <h2 className="green-to-red-gradient-font">Course Not Found</h2>
                    <p>The course you're looking for could not be found or is still loading.</p>
                    <div className="breadcrumb-navigation">
                        <Link to="/">Home</Link>
                        <span className="breadcrumb-separator">/</span>
                        <Link to="/training">Training</Link>
                        <span className="breadcrumb-separator">/</span>
                        <span className="current-page">Course Not Found</span>
                    </div>
                </div>
            </div>
        );
    }

    return (   <>
      <Helmet>
        <title>
          {course?.title
            ? `${course.title} Training Course${course?.duration ? ` | ${course.duration}-Day Program` : ''} | Watchelm`
            : 'Course | Watchelm'}
        </title>
        <meta name="description" content={
          course?.metaDescription || 
          (course?.title ? `Learn ${course.title} with expert-led training from Watchelm. Professional technical training courses designed for enterprise teams.` : 'Expert technical training courses from Watchelm')
        } />
        {/* Add canonical link for SEO - use URL params directly so it's available before async data loads */}
        <link
          rel="canonical"
          href={`https://www.watchelm.com/course/${encodeURIComponent(code)}/${encodeURIComponent(title)}`}
        />
        {courseSchema && Object.keys(courseSchema).length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify(courseSchema)}
          </script>
        )}  
      </Helmet>
          <div className="flow-page">
       <div className="section-container">          
          
          <div className="breadcrumb-navigation">
            <Link to="/">Home</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to="/training">Training</Link>
            {sourceCategoryName && (
              <>
                <span className="breadcrumb-separator">/</span>
                <Link to={`/categories/${sourceCategoryName}`}>
                  {formatCategoryName(sourceCategoryName)}
                </Link>
              </>
            )}
            <span className="breadcrumb-separator">/</span>
            <span className="current-page">{course.title}</span>
          </div>            <div className="course-content-container">            <div className="course-content-main">
              <h1 className="green-to-red-gradient-font">
                {course.title}
                {course.duration && (
                  <> ({course.duration} {parseInt(course.duration) === 1 ? 'day' : 'days'})</>
                )}
              </h1>
              <p>{course.outline}</p>{course.objectives && (
                <>
                <h2 className="green-to-red-gradient-font">Objectives</h2>
                <ul>
                    {course.objectives.map((objective, index) => (
                <li key={index}>{objective}</li>
                ))}
            </ul>
            </>
          )}          {course.prerequisites && (
            <>
            <h2 className="green-to-red-gradient-font">Prerequisites</h2>
            <ul>
                {course.prerequisites.map((prerequisite, index) => (
                <li key={index}>{prerequisite}</li>
                ))}
            </ul>
            </>
          )}{course.contents && (
            <>
            <h2 className="green-to-red-gradient-font">Contents</h2>

                {course.contents.map((chapter, index) => (
                  <div key={index}>
                  <h4>{chapter.title}</h4>
                  {chapter.objective && chapter.objective.length > 0 && (
                    <ul>
                       {chapter.objective.map((objective, objIndex) => (
                            <li key={objIndex}>{objective}</li>
                       ))}
                    </ul>
                  )}
                  </div>
                ))}
          
            </>
          )}
            </div>
            <div className="course-content-sidebar">
              <CourseContactForm courseName={course.title} />
            </div>          </div>
        </div>
        {/* Mobile contact form - displayed below content on mobile devices */}
        <div className="mobile-course-contact-container">
          <CourseContactForm courseName={course.title} isMobile={true} />
        </div>
      </div>
    );    
    
  </>);
}
export default Course;